// Pure constants and helpers shared by the two renders of project detail —
// the desktop rail in pages/ProjectDetail.tsx and the mobile page in
// components/ProjectDetailMobile.tsx (mobile overhaul Phase B §6). They were
// module-scope definitions in ProjectDetail.tsx; they live here so the mobile
// component can consume them without importing the page (which would form an
// ESM cycle, the same trap lib/calendar.ts was carved out of in Phase 0).
//
// No React, no DOM, no component imports.

import { PHASE_LABELS, type InvoiceRow, type ProjectPhase, type SocialStatus } from '../../shared/types';
import { addDays, formatDayMonth } from './calendar';
import { formatMoney, parseMoney } from './moneyFormat';

export const DASHBOARD_BASE = 'https://dashboard.sheahanstudios.com';

/** The six stepper phases (closed / on_hold sit outside the pipeline). */
export const STEPS: ProjectPhase[] = ['lead', 'pre-production', 'shoot', 'post', 'delivered', 'clean_up'];
export const STEP_LABELS: Record<string, string> = {
  'lead': 'Onboarding',
  'pre-production': 'Pre-prod',
  'shoot': 'Shoot',
  'post': 'Post',
  'delivered': 'Delivered',
  'clean_up': 'Clean up',
};

export const PHASE_DOTS: Record<ProjectPhase, string> = {
  'lead': 'var(--text-faint)',
  'pre-production': 'var(--accent-deep)',
  'shoot': 'var(--gold)',
  'post': 'var(--accent)',
  'delivered': 'var(--ok)',
  'clean_up': 'linear-gradient(90deg, var(--ok) 50%, color-mix(in srgb, var(--ok) 22%, transparent) 50%)',
  'closed': 'var(--text-faint)',
  'on_hold': 'var(--text-faint)',
};

export type PhasePillTone = 'accent' | 'gold' | 'cleanup' | undefined;

export function pillTone(phase: ProjectPhase): PhasePillTone {
  if (phase === 'clean_up') return 'cleanup';
  return phase === 'post' || phase === 'delivered' ? 'accent' : phase === 'shoot' ? 'gold' : undefined;
}

export function socialTone(s: SocialStatus | null): PhasePillTone {
  if (!s) return undefined;
  if (s === 'posted' || s === 'content_approved') return 'accent';
  if (s === 'waiting_approval' || s === 'post_soon' || s === 'request_permission') return 'gold';
  return undefined;
}

// Tools rail (Wave A1 — the artifacts spine): project_artifacts rows read
// first, per-tool; the legacy manual-paste *_url fields on the project stay
// as fallback for tools that haven't been linked yet.
export const TOOL_MAP = [
  { tool: 'script' as const, urlField: 'script_url' as const, route: 'scripts', label: 'Script' },
  { tool: 'callsheet' as const, urlField: 'callsheet_url' as const, route: 'callsheets', label: 'Call sheet' },
  { tool: 'proposal' as const, urlField: 'proposal_url' as const, route: 'proposals', label: 'Proposal' },
  { tool: 'review' as const, urlField: null, route: 'reviews', label: 'Review' },
];

export const CREATE_TOOL_ROUTES = {
  script: 'scripts',
  callsheet: 'callsheets',
  review: 'reviews',
} as const;

// Artifact status values are an opaque tool-owned passthrough.
// project_artifacts applies no CHECK, so labels and tones fall back gracefully
// for anything unknown.
const ARTIFACT_STATUS_LABELS: Record<string, string> = {
  approved: 'Approved',
  in_review: 'In review',
  draft: 'Draft',
  sent: 'Sent',
  signed: 'Signed',
  published: 'Published',
  archived: 'Archived',
  pending: 'Pending',
  paid: 'Paid',
  unpaid: 'Unpaid',
};

export function artifactStatusLabel(status: string): string {
  return ARTIFACT_STATUS_LABELS[status] ?? status.replace(/_/g, ' ');
}

export function artifactStatusTone(status: string): PhasePillTone {
  if (status === 'approved' || status === 'paid' || status === 'signed') return 'accent';
  if (status === 'in_review' || status === 'unpaid') return 'gold';
  return undefined;
}

export function invoiceState(inv: InvoiceRow, today: string): { text: string; cls: string } {
  if (inv.status === 'paid') return { text: 'Paid', cls: ' paid' };
  if (inv.status === 'overdue' || (inv.status === 'sent' && inv.due_date && inv.due_date < today)) {
    const days = inv.due_date
      ? Math.max(1, Math.round((Date.parse(today) - Date.parse(inv.due_date)) / 86400000))
      : null;
    return { text: days ? `${days}d overdue` : 'Overdue', cls: ' overdue' };
  }
  if (inv.status === 'sent') return { text: 'Sent', cls: '' };
  if (inv.status === 'void') return { text: 'Void', cls: '' };
  return { text: 'Draft', cls: '' };
}

/** True for an invoice that is past its due date and unpaid — the red part
    of the Money accordion summary. */
export function invoiceOverdue(inv: InvoiceRow, today: string): boolean {
  return invoiceState(inv, today).cls === ' overdue';
}

// "On this job" crew block (Phase 3.3, mockup #4) — "12–13 Jul" within a
// month, "28 Jun – 2 Jul" across months, a single day collapses to one date.
export function crewDateLabel(start: string, end: string): string {
  if (start === end) return formatDayMonth(start);
  if (start.slice(0, 7) === end.slice(0, 7)) {
    return `${Number(start.slice(8, 10))}–${formatDayMonth(end)}`;
  }
  return `${formatDayMonth(start)} – ${formatDayMonth(end)}`;
}

/** Distinct calendar days covered by any booking — the crew-total footer's
    "M-day shoot" is the union across entries, not a sum (overlapping people
    on the same day still count as one shoot day). */
export function crewBookedDays(bookings: { start_date: string; end_date: string }[]): number {
  const days = new Set<string>();
  for (const b of bookings) {
    for (let d = b.start_date; d <= b.end_date; d = addDays(d, 1)) days.add(d);
  }
  return days.size;
}

/** Every calendar date (YYYY-MM-DD) in an inclusive range — the per-day chip
    row's (§4b) source of truth. */
export function dateRange(start: string, end: string): string[] {
  const dates: string[] = [];
  for (let d = start; d <= end; d = addDays(d, 1)) dates.push(d);
  return dates;
}

/** Keeps half-day selections that fall inside a [start, end] range, drops
    the rest — mirrors the backend's own check (routes/projects.ts's
    validateHalfDayDates) so the UI never submits a half day the server
    would 400 on after the range shrinks. */
export function reconcileHalfDayDates(dates: string[], start: string, end: string): string[] {
  return dates.filter((d) => d >= start && d <= end);
}

/** A booking's half_day_dates column is a raw JSON string (or null) as
    returned from D1. Duplicates crewCost.ts's internal parser — that one
    isn't exported, and this build stays out of src/shared, so the client
    keeps its own tiny defensive copy. */
export function parseHalfDayDates(raw: string | null): string[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : [];
  } catch {
    return [];
  }
}

/** 50% of a day-rate draft string, rounded to the nearest cent — the dim
    "suggested: $X" hint Henry's spec requires. Never auto-filled into a
    real field, only ever shown as a placeholder/label while it's empty. */
export function halfSuggestionCents(dayRateDraft: string): number | null {
  const cents = parseMoney(dayRateDraft);
  return cents === null ? null : Math.round(cents / 2);
}

/** "$1,400 · 2 days + 1 half" per-booking cost line (§4c), from
    bookingCostCents' full/half split — the half clause is omitted entirely
    when there are no half days. */
export function crewCalcLabel(cost: { full_days: number; half_days: number; total_cents: number }): string {
  const dayLabel = `${cost.full_days} day${cost.full_days === 1 ? '' : 's'}`;
  const halfLabel = cost.half_days > 0 ? ` + ${cost.half_days} half${cost.half_days === 1 ? '' : 'ves'}` : '';
  return `${formatMoney(cost.total_cents)} · ${dayLabel}${halfLabel}`;
}

/** Rail footer sub-line (§4c) — "N people · M-day shoot" stays exactly as
    it reads today while no booking on the job has any half day; once one
    exists anywhere it switches to "N people · M days · K half". */
export function crewFooterSub(people: number, days: number, halfCount: number): string {
  const peopleLabel = `${people} ${people === 1 ? 'person' : 'people'}`;
  if (halfCount === 0) return `${peopleLabel} · ${days}-day shoot`;
  const dayLabel = `${days} ${days === 1 ? 'day' : 'days'}`;
  const halfLabel = `${halfCount} ${halfCount === 1 ? 'half' : 'halves'}`;
  return `${peopleLabel} · ${dayLabel} · ${halfLabel}`;
}

export const CREW_COST_ERROR_TITLE =
  'A half day on this job has no half-day rate on file — edit the booking to add one.';

export function waitingNextStatus(
  project: {
    waiting_snoozed_until: string | null;
    waiting_nudge_count: number;
    waiting_on_client_since: number | null;
    waiting_last_nudge_at: number | null;
  },
  today: string,
): string {
  if (project.waiting_snoozed_until && project.waiting_snoozed_until >= today) {
    return `Snoozed until ${formatDayMonth(project.waiting_snoozed_until)}`;
  }
  if (project.waiting_nudge_count >= 3) return 'Automatic nudges complete';
  const base = project.waiting_nudge_count === 0
    ? project.waiting_on_client_since
    : project.waiting_last_nudge_at;
  const waitDays = project.waiting_nudge_count === 0 ? 4 : 7;
  const remaining = base === null ? waitDays : Math.max(0, Math.ceil((base + waitDays * 86_400_000 - Date.now()) / 86_400_000));
  return remaining === 0 ? 'Next nudge scheduled tonight' : `Next nudge in ${remaining}d`;
}

// Contact rail picker (mockups-feature-builds §3b) — the client's live
// contacts plus a sentinel "Custom…" option that reveals the free-text
// override fields projects have always had.
export const CUSTOM_CONTACT_VALUE = '__custom__';

/** "Sarah Chen — Marketing lead" (+ "(default)") — dropdown list label. */
export function contactOptionLabel(
  contact: { name: string; role_label: string | null },
  isDefault: boolean,
): string {
  const withRole = contact.role_label ? `${contact.name} — ${contact.role_label}` : contact.name;
  return isDefault ? `${withRole} (default)` : withRole;
}

/** "Shoot → Post" — the eyebrow over the phase confirm sheet. */
export function phaseMoveArrow(from: ProjectPhase, to: ProjectPhase): string {
  return `${PHASE_LABELS[from]} → ${PHASE_LABELS[to]}`;
}

/** The one-line consequence under "Move to {Phase}?" (decision 8, mockup
    §6.3 I). Only ever states what the app actually does: the phase PATCH's
    single server-side effect is stamping delivered_date on the first move
    into Delivered (worker/lib/projectPhase.ts) — everything else is a
    change of where the project shows up, which is still worth naming
    because a mis-tap on the stepper is expensive. */
export function phaseMoveConsequence(from: ProjectPhase, to: ProjectPhase): string {
  if (to === 'delivered') return 'Stamps the delivered date as today and starts the testimonial ask.';
  if (to === 'clean_up') return 'The project leaves Active and waits under Projects → Clean up. The delivered date stays.';
  if (to === 'closed' && from === 'clean_up') return 'Next, confirm where the footage is backed up.';
  if (from === 'delivered') return 'The recorded delivered date stays as it is.';
  if (to === 'post') return 'The primary action becomes "Cut approved →".';
  if (to === 'on_hold') return 'The project leaves the active shelves, the overview and the nudges.';
  if (to === 'closed') return 'The project closes without being marked delivered.';
  return `The project moves to the ${PHASE_LABELS[to]} shelf on Projects. Nothing else changes.`;
}
